import { marked } from "marked";

/**
 * Strips markdown formatting from text and returns plain text
 * Converts markdown to HTML, then extracts text content using DOM parser
 * @param markdown - The markdown text to strip
 * @returns Plain text without markdown formatting
 */
export const stripMarkdown = (markdown: string): string => {
  if (!markdown) return "";

  try {
    // Convert markdown to HTML using marked
    const html = marked.parse(markdown) as string;

    // Create a temporary div element to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Extract text content (automatically strips all HTML tags)
    const plainText = (tempDiv.textContent ?? tempDiv.innerText) || "";

    // Clean up extra whitespace and return
    return plainText.replace(/\s+/g, " ").trim();
  } catch (error) {
    console.error("Error stripping markdown:", error);
    // Fallback: return original text with basic cleanup if parsing fails
    return markdown.replace(/\s+/g, " ").trim();
  }
};

// Known HTML tag names that marked.parse and our AI draft pipeline legitimately
// emit. A legal draft can contain placeholders like <Client Name>, <Date>, or
// <Effective Date>, which look tag-like but must NOT bypass the markdown→HTML
// conversion path. Restricting to a whitelist of known tag names (with word
// boundaries) keeps those placeholders out. Tag match is case-insensitive so
// `<P>` and `<Div>` still count.
const HTML_TAG_RE =
  /<\/?(?:p|div|span|h[1-6]|ul|ol|li|table|thead|tbody|tfoot|tr|td|th|br|hr|img|a|strong|em|b|i|u|code|pre|blockquote|section|article|header|footer|nav|main|aside|figure|figcaption|dl|dt|dd|mark|sub|sup|small|cite|q|s|del|ins|hr)\b[^<>]*>/i;

/**
 * Checks if content is HTML that should be inserted as-is (skipping markdown
 * conversion). Matches only recognised HTML tag names so legal placeholders
 * like `<Client Name>` or `<Effective Date>` are not misclassified as HTML.
 *
 * @param content - The content to check
 * @returns true if the content contains at least one known HTML tag
 */
export const isHtmlContent = (content: string): boolean => {
  return HTML_TAG_RE.test(content.trim());
};

/**
 * Converts markdown text to HTML
 * @param markdown - The markdown text to convert
 * @returns HTML string
 */
export const convertMarkdownToHtml = (markdown: string): string => {
  if (!markdown) return "";
  try {
    return marked.parse(markdown) as string;
  } catch (error) {
    console.error("Error converting markdown to HTML:", error);
    return markdown;
  }
};
